import * as React from 'react'
import {
  FolderTree,
  History,
  Inbox,
  Plus,
  RefreshCw,
  RotateCcw,
  Table2,
  Trash2
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  useListIngestIntakesQuery,
  useListIngestMappingConfigsQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { useAgentIngestSession } from '../../../components/Chat/useAgentIngestSession'
import { useOffBranchUnstructuredNeedsInput } from '../../../components/Chat/useOffBranchUnstructuredNeedsInput'
import {
  INGEST_SESSION_FOCUS_STATE,
  ingestFullSessionPath
} from '../../../components/IngestSession/ingest-session-route'
import { useDocumentUpload } from '../../../components/IngestSession/DocumentUploadProvider'
import { Button } from '../../../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group'
import { JobsTable, type JobsScope } from '../components/JobsTable'
import { IngestedFilesPanel } from '../components/IngestedFilesPanel'
import { ConfigsTab } from '../configs'
import { configRows } from '../configs/config-view'
import { IntakesTab } from '../intakes'
import { PssSyncTab } from '../pss-sync'
import { WizardPageHeader } from './WizardChrome'
import { siteOptionLabel } from './siteIds'

export type LandingTab = 'runs' | 'files' | 'intakes' | 'configs' | 'pss-sync'

export interface LandingSite {
  id: string
  name: string
}

interface IngestLandingProps {
  scope: JobsScope
  activeBranchId?: string
  sites: LandingSite[]
  showIntakes?: boolean
  initialTab?: LandingTab
  onTabChange?: (tab: LandingTab) => void
  onScopeChange: (scope: JobsScope) => void
  onStartWizard: () => void
  drafts?: Array<{
    id: string
    siteName: string
    step: number
    paused: boolean
    running: boolean
    updatedAt: number
  }>
  onResumeWizard?: (id: string) => void
  onDiscardWizard?: (id: string) => void
}

export function IngestLanding({
  scope,
  activeBranchId,
  sites,
  showIntakes = false,
  initialTab = 'runs',
  onTabChange,
  onScopeChange,
  onStartWizard,
  drafts = [],
  onResumeWizard,
  onDiscardWizard
}: IngestLandingProps) {
  const intakesQuery = useListIngestIntakesQuery(undefined, {
    skip: !showIntakes
  })
  const intakeCount = intakesQuery.data?.intakes.length ?? 0
  // Same arguments as the Configs tab's own query, so RTK serves both from one request.
  const configsQuery = useListIngestMappingConfigsQuery({
    kind: 'mapped_tabular_ingest',
    include_archived: true,
    limit: 500
  })
  const configCount = React.useMemo(
    () => configRows(configsQuery.data ?? []).filter((row) => !row.archived).length,
    [configsQuery.data]
  )
  // The tab exists once any config row does; archived ones count so they stay
  // reachable. While the list loads, a `?tab=configs` link is held rather than
  // bounced to Runs.
  const hasConfigs = (configsQuery.data?.length ?? 0) > 0
  const configsTabAvailable = hasConfigs || configsQuery.isLoading
  const allowedTab = React.useCallback(
    (requested: LandingTab): LandingTab =>
      (requested === 'intakes' && !showIntakes) || (requested === 'configs' && !configsTabAvailable)
        ? 'runs'
        : requested,
    [configsTabAvailable, showIntakes]
  )
  const [tab, setTab] = React.useState<LandingTab>(() => allowedTab(initialTab))
  const [siteFilter, setSiteFilter] = React.useState<string>(() => sites[0]?.id ?? '')
  const [intakeCreateOpen, setIntakeCreateOpen] = React.useState(false)
  const { openDocumentUpload, documentTransferOpen } = useDocumentUpload()
  const { session, isActive } = useAgentIngestSession()
  const offBranchNeedsInput = useOffBranchUnstructuredNeedsInput({
    skip: Boolean(session)
  })
  const openSession = session ?? offBranchNeedsInput.session
  const runningUnstructured = isActive ? session : undefined

  // The page owns the tab in the URL; follow it when a deep link or Back
  // changes it while this landing stays mounted.
  React.useEffect(() => {
    setTab(allowedTab(initialTab))
  }, [allowedTab, initialTab])

  React.useEffect(() => {
    if (allowedTab(tab) !== tab) {
      setTab('runs')
      onTabChange?.('runs')
    }
  }, [allowedTab, onTabChange, tab])

  // `sites` is the active branch's graph catalog, already deduped by the
  // page. Job rows and object-store prefixes are deliberately not merged in:
  // they outlive branch deletion and would offer sites that no longer exist.
  const siteOptions = sites

  // Keep the selection valid when the catalog changes (branch switch, site
  // added or removed): fall back to the first site, or clear when none remain.
  React.useEffect(() => {
    if (siteOptions.length === 0) {
      if (siteFilter) setSiteFilter('')
      return
    }
    if (!siteOptions.some((s) => s.id === siteFilter)) {
      setSiteFilter(siteOptions[0]!.id)
    }
  }, [siteOptions, siteFilter])

  const selectedSite = siteOptions.find((s) => s.id === siteFilter)
  const siteName = selectedSite ? siteOptionLabel(selectedSite.name, selectedSite.id) : 'this site'
  const siteNames = React.useMemo(
    () => Object.fromEntries(siteOptions.map((s) => [s.id, siteOptionLabel(s.name, s.id)])),
    [siteOptions]
  )
  const subtitle =
    tab === 'pss-sync'
      ? 'Monitor PlantState Suite journal refreshes and configure their update frequency.'
      : tab === 'intakes'
        ? 'Manage named upload addresses, targets, branches, and received files.'
        : tab === 'configs'
          ? 'Every tabular config: its latest version, any draft waiting for review, and the intakes that run it.'
          : tab === 'files'
            ? siteOptions.length === 0
              ? 'No sites yet. Start a new ingest to add one, then browse its files here.'
              : `Everything ingested for ${siteName}, grouped by source.`
            : siteOptions.length === 0
              ? 'Past ingest runs. Open a run to see the jobs it dispatched.'
              : `Past ingest runs for ${siteName}. Open a run to see the jobs it dispatched.`

  return (
    <div className="mx-auto flex w-full max-w-[68rem] flex-col gap-5">
      <WizardPageHeader
        title="Ingest"
        subtitle={subtitle}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {openSession ? (
              <Button asChild variant="outline">
                <Link
                  to={ingestFullSessionPath(openSession.id, {
                    questionId: offBranchNeedsInput.questionId
                  })}
                  state={INGEST_SESSION_FOCUS_STATE}
                >
                  {session ? 'Open ingest session' : 'Needs input — switch branch'}
                </Link>
              </Button>
            ) : null}
            {runningUnstructured ? (
              <Button
                type="button"
                variant="outline"
                disabled={documentTransferOpen}
                onClick={() =>
                  openDocumentUpload({
                    mode: 'add',
                    jobId: runningUnstructured.id,
                    contextJobId: runningUnstructured.id
                  })
                }
              >
                Ingest documents
              </Button>
            ) : null}
            {tab === 'runs' ? (
              <Button type="button" onClick={onStartWizard}>
                <Plus className="size-4" />
                New Ingest
              </Button>
            ) : null}
            {tab === 'intakes' && showIntakes ? (
              <Button type="button" onClick={() => setIntakeCreateOpen(true)}>
                <Plus className="size-4" />
                New intake
              </Button>
            ) : null}
          </div>
        }
      />

      {tab === 'runs' && drafts.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div className="text-11 font-medium tracking-[0.08em] text-ink-3 uppercase">
            Active and saved ingests
          </div>
          {drafts.map((draft) => (
            <div
              key={draft.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-st-info/30 bg-st-info-bg px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ink">
                  {draft.running ? 'Open ingest for ' : 'Resume ingest for '}
                  {draft.siteName || 'this site'}
                </div>
                <div className="mt-0.5 text-xs text-ink-3">
                  {draft.running
                    ? 'Jobs are running independently.'
                    : `Saved at step ${draft.step} of 4${
                        draft.paused ? ' after a failed job' : ''
                      }.`}
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onDiscardWizard?.(draft.id)}
              >
                <Trash2 className="size-3.5" />
                Discard
              </Button>
              <Button type="button" size="sm" onClick={() => onResumeWizard?.(draft.id)}>
                <RotateCcw className="size-3.5" />
                Open
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      {tab === 'runs' || tab === 'files' ? (
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={siteFilter || undefined}
            onValueChange={setSiteFilter}
            disabled={siteOptions.length === 0}
          >
            <SelectTrigger size="sm" className="h-8 w-65 text-13" aria-label="Site filter">
              <SelectValue
                placeholder={siteOptions.length === 0 ? 'No sites yet' : 'Select a site'}
              />
            </SelectTrigger>
            <SelectContent>
              {siteOptions.map((site) => (
                <SelectItem key={site.id} value={site.id}>
                  {siteOptionLabel(site.name, site.id)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <Tabs
        value={tab}
        onValueChange={(value) => {
          if (
            value === 'runs' ||
            value === 'files' ||
            (value === 'intakes' && showIntakes) ||
            (value === 'configs' && hasConfigs) ||
            value === 'pss-sync'
          ) {
            setTab(value)
            onTabChange?.(value)
          }
        }}
        className="gap-4"
      >
        <TabsList variant="line">
          <TabsTrigger value="runs" className="gap-1.5">
            <History className="size-3.5" />
            Runs
          </TabsTrigger>
          <TabsTrigger value="files" className="gap-1.5">
            <FolderTree className="size-3.5" />
            Ingested files
          </TabsTrigger>
          {showIntakes ? (
            <TabsTrigger value="intakes" className="gap-1.5">
              <Inbox className="size-3.5" />
              Intake endpoints
              {intakeCount > 0 ? ` (${intakeCount})` : ''}
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="pss-sync" className="gap-1.5">
            <RefreshCw className="size-3.5" />
            PSS sync
          </TabsTrigger>
          {hasConfigs ? (
            <TabsTrigger value="configs" className="gap-1.5">
              <Table2 className="size-3.5" />
              Tabular configs
              {configCount > 0 ? ` (${configCount})` : ''}
            </TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent
          value="runs"
          className="mt-0 flex flex-col gap-3 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-200 motion-reduce:data-[state=active]:animate-none"
        >
          <div className="flex flex-wrap items-center gap-3">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={scope}
              onValueChange={(value) => {
                if (value === 'branch' || value === 'all') onScopeChange(value)
              }}
              aria-label="Branch scope"
            >
              <ToggleGroupItem value="branch" className="h-7 px-2.5 text-11 font-medium">
                On this branch
              </ToggleGroupItem>
              <ToggleGroupItem value="all" className="h-7 px-2.5 text-11 font-medium">
                All branches
              </ToggleGroupItem>
            </ToggleGroup>
            {scope === 'all' && intakeCount > 0 ? (
              <p className="text-11 text-muted-foreground" data-testid="runs-intake-hint">
                Files sent to intake endpoints are listed under{' '}
                <button
                  type="button"
                  className="font-medium text-foreground underline-offset-2 hover:underline"
                  onClick={() => {
                    setTab('intakes')
                    onTabChange?.('intakes')
                  }}
                >
                  Intake endpoints
                </button>
                .
              </p>
            ) : null}
          </div>
          <JobsTable
            scope={scope}
            activeBranchId={activeBranchId}
            siteNames={siteNames}
            siteId={siteFilter || undefined}
            siteName={selectedSite?.name}
          />
        </TabsContent>

        <TabsContent
          value="files"
          className="mt-0 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-200 motion-reduce:data-[state=active]:animate-none"
        >
          {!siteFilter ? (
            <div className="rounded-md border border-line bg-bg-elev px-4 py-6 text-sm text-ink-3">
              No sites yet. Start a new ingest to add one, then browse its files here.
            </div>
          ) : (
            <IngestedFilesPanel siteId={siteFilter} siteName={siteName} />
          )}
        </TabsContent>

        {showIntakes ? (
          <TabsContent
            value="intakes"
            className="mt-0 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-200 motion-reduce:data-[state=active]:animate-none"
          >
            <IntakesTab
              sites={sites}
              createOpen={intakeCreateOpen}
              onCreateOpenChange={setIntakeCreateOpen}
            />
          </TabsContent>
        ) : null}

        <TabsContent
          value="pss-sync"
          className="mt-0 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-200 motion-reduce:data-[state=active]:animate-none"
        >
          <PssSyncTab />
        </TabsContent>
        {configsTabAvailable ? (
          <TabsContent
            value="configs"
            className="mt-0 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-200 motion-reduce:data-[state=active]:animate-none"
          >
            <ConfigsTab linkIntakes={showIntakes} />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  )
}
